import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const TestimonialsCarousel = ({ currentLanguage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const content = {
    es: {
      title: "Lo que dicen nuestros clientes",
      subtitle: "Testimonios reales de familias satisfechas"
    },
    en: {
      title: "What our clients say",
      subtitle: "Real testimonials from satisfied families"
    }
  };

  const testimonials = [
    {
      id: 1,
      nameEs: "María González",
      nameEn: "María González",
      locationEs: "Escazú, San José",
      locationEn: "Escazú, San José",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      ratingEs: 5,
      ratingEn: 5,
      reviewEs: `Excelente servicio. Llevan más de 2 años manteniendo nuestra piscina y siempre está perfecta. Los técnicos son muy profesionales y puntuales. Recomiendo BAC Pool CR al 100%.`,
      reviewEn: `Excellent service. They have been maintaining our pool for over 2 years and it's always perfect. The technicians are very professional and punctual. I recommend BAC Pool CR 100%.`,
      serviceEs: "Limpieza Semanal",
      serviceEn: "Weekly Cleaning"
    },
    {
      id: 2,
      nameEs: "Carlos Rodríguez",
      nameEn: "Carlos Rodríguez",
      locationEs: "Santa Ana, San José",
      locationEn: "Santa Ana, San José",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      ratingEs: 5,
      ratingEn: 5,
      reviewEs: `Tuvimos una emergencia con la bomba de la piscina un domingo y vinieron inmediatamente. Solucionaron el problema rápidamente y a un precio justo. Muy recomendados.`,
      reviewEn: `We had an emergency with the pool pump on a Sunday and they came immediately. They solved the problem quickly and at a fair price. Highly recommended.`,
      serviceEs: "Servicio de Emergencia",
      serviceEn: "Emergency Service"
    },
    {
      id: 3,
      nameEs: "Ana Jiménez",
      nameEn: "Ana Jiménez",
      locationEs: "Curridabat, San José",
      locationEn: "Curridabat, San José",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      ratingEs: 5,
      ratingEn: 5,
      reviewEs: `El mejor servicio de limpieza de piscinas que hemos tenido. Son muy detallistas y siempre dejan todo impecable. Además, nos dan consejos útiles para el mantenimiento.`,
      reviewEn: `The best pool cleaning service we've had. They are very detail-oriented and always leave everything spotless. They also give us useful maintenance tips.`,
      serviceEs: "Mantenimiento Completo",
      serviceEn: "Complete Maintenance"
    }
  ];

  const text = content[currentLanguage];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, [testimonials.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1);
  };

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
            {text.title}
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            {text.subtitle}
          </p>
        </div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="overflow-hidden rounded-lg">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="w-full flex-shrink-0">
                  <div className="bg-background border border-border rounded-lg p-8 mx-4">
                    {/* Rating */}
                    <div className="flex items-center justify-center mb-6">
                      {[...Array(5)].map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={20}
                          className={i < (currentLanguage === 'es' ? testimonial.ratingEs : testimonial.ratingEn) 
                            ? "text-warning fill-current" :"text-border"
                          }
                        />
                      ))}
                    </div>

                    {/* Review */}
                    <blockquote className="text-lg text-text-primary text-center mb-8 italic">
                      "{currentLanguage === 'es' ? testimonial.reviewEs : testimonial.reviewEn}"
                    </blockquote>

                    {/* Customer Info */}
                    <div className="flex items-center justify-center space-x-4">
                      <Image
                        src={testimonial.avatar}
                        alt={currentLanguage === 'es' ? testimonial.nameEs : testimonial.nameEn}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="text-center">
                        <div className="font-semibold text-text-primary">
                          {currentLanguage === 'es' ? testimonial.nameEs : testimonial.nameEn}
                        </div>
                        <div className="text-sm text-text-secondary">
                          {currentLanguage === 'es' ? testimonial.locationEs : testimonial.locationEn}
                        </div>
                        <div className="text-xs text-primary font-medium">
                          {currentLanguage === 'es' ? testimonial.serviceEs : testimonial.serviceEn}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-white nav-transition shadow-medium"
            aria-label="Previous testimonial"
          >
            <Icon name="ChevronLeft" size={20} />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-12 h-12 bg-surface border border-border rounded-full flex items-center justify-center hover:bg-primary hover:text-white nav-transition shadow-medium"
            aria-label="Next testimonial"
          >
            <Icon name="ChevronRight" size={20} />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full nav-transition ${
                  index === currentIndex ? 'bg-primary' : 'bg-border'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;